/**
 * BirdsSpawner.ts — schedules occasional bird flyovers around the player.
 *
 * Spawns either a single gull or a small V-flock (3..6 birds) at random
 * intervals (60..180 s). Each flock flies in a straight line at constant
 * speed/altitude across a corridor centred on the player and despawns when
 * it leaves the visible range.
 *
 * Packs per-bird instance data into a Float32Array consumed by BirdsPass.
 */

/** Bytes per instance: 2 vec4 = 32 B. Keep in sync with BirdsPass. */
export const BIRD_STRIDE_BYTES = 32;
/** Hard cap on simultaneously active birds (3 flocks × ≤6 birds + margin). */
export const BIRDS_CAPACITY = 32;

interface Bird {
  /** Flap phase offset (radians). */
  phase: number;
  /** Local offset in flock-aligned frame: along, side (left+), up (m). */
  offAlong: number;
  offSide: number;
  offUp: number;
}

interface Flight {
  /** Flock leader world position. */
  headX: number;
  headY: number;
  headZ: number;
  /** Horizontal velocity (m/s). */
  velX: number;
  velZ: number;
  /** Cached precomputed yaw = atan2(-velZ, velX) for shader rotation. */
  yaw: number;
  /** Cached forward/side unit vectors. */
  fwdX: number;
  fwdZ: number;
  sideX: number;
  sideZ: number;
  /** Per-bird scale (m). Larger = bigger silhouette. */
  scale: number;
  /** Lifetime tracking. */
  elapsed: number;
  duration: number;
  /** Members of the flock. */
  birds: Bird[];
}

export class BirdsSpawner {
  private flights: Flight[] = [];
  private nextSpawnIn = 8 + Math.random() * 12; // first spawn after 8..20 s
  /** Backing storage written to the GPU buffer (ArrayBuffer, not a view). */
  private readonly cpuArrayBuffer = new ArrayBuffer(
    BIRDS_CAPACITY * BIRD_STRIDE_BYTES,
  );
  private readonly instanceCpu = new Float32Array(this.cpuArrayBuffer);
  private _instanceCount = 0;

  /** How many bird instances are currently packed in {@link cpuBuffer}. */
  get instanceCount(): number {
    return this._instanceCount;
  }

  /** Raw per-instance buffer (32 B per instance) ready for writeBuffer. */
  get cpuBuffer(): ArrayBuffer {
    return this.cpuArrayBuffer;
  }

  /**
   * Advance flights, despawn finished ones, possibly spawn a new flight,
   * and repack the instance buffer. Pass the player's horizontal position
   * so flocks appear around them.
   */
  step(dt: number, waterLevel: number, centerX: number, centerZ: number): void {
    // Advance.
    for (const f of this.flights) {
      f.headX += f.velX * dt;
      f.headZ += f.velZ * dt;
      f.elapsed += dt;
    }
    // Cull finished flights.
    this.flights = this.flights.filter((f) => f.elapsed < f.duration);

    // Schedule next spawn.
    this.nextSpawnIn -= dt;
    if (this.nextSpawnIn <= 0 && this.flights.length < 3) {
      this.spawnFlight(waterLevel, centerX, centerZ);
      // 60..180 s until the next one.
      this.nextSpawnIn = 60 + Math.random() * 120;
    }

    // Repack instance buffer.
    let n = 0;
    for (const f of this.flights) {
      for (const b of f.birds) {
        if (n >= BIRDS_CAPACITY) break;
        const px = f.headX + f.fwdX * b.offAlong + f.sideX * b.offSide;
        const pz = f.headZ + f.fwdZ * b.offAlong + f.sideZ * b.offSide;
        const py = f.headY + b.offUp;
        // Soft fade in/out at the edges of the flight so birds don't pop.
        const t = f.elapsed / f.duration;
        const fade = Math.min(
          Math.min(t / 0.08, 1.0),
          Math.min((1.0 - t) / 0.12, 1.0),
        );
        const off = n * 8;
        // posYaw
        this.instanceCpu[off + 0] = px;
        this.instanceCpu[off + 1] = py;
        this.instanceCpu[off + 2] = pz;
        this.instanceCpu[off + 3] = f.yaw;
        // packed: scale, flapPhase, flapAmp, alpha
        this.instanceCpu[off + 4] = f.scale;
        this.instanceCpu[off + 5] = b.phase;
        this.instanceCpu[off + 6] = 0.55; // flap amplitude (rad-ish bend factor)
        this.instanceCpu[off + 7] = Math.max(0, fade);
        n++;
      }
    }
    this._instanceCount = n;
  }

  /** Force-clear all active flights (e.g. when the toggle goes off). */
  clear(): void {
    this.flights.length = 0;
    this._instanceCount = 0;
  }

  /**
   * Spawn a flock right now (driven from the UI "release birds" button).
   * Always a small V-flock for a dramatic, visible result; capped by the
   * 3-flight ceiling so spamming the button can't flood the buffer.
   */
  releaseNow(waterLevel: number, centerX: number, centerZ: number): void {
    if (this.flights.length >= 3) return;
    this.spawnFlight(waterLevel, centerX, centerZ, {
      forceFlock: true,
      closeSpawn: true,
    });
  }

  private spawnFlight(
    waterLevel: number,
    centerX: number,
    centerZ: number,
    opts: { forceFlock?: boolean; closeSpawn?: boolean } = {},
  ): void {
    // Random heading; cross a corridor through the player area.
    const heading = Math.random() * Math.PI * 2;
    const fwdX = Math.cos(heading);
    const fwdZ = Math.sin(heading);
    // Left-perpendicular (90° CCW in XZ).
    const sideX = -fwdZ;
    const sideZ = fwdX;

    // Close-range spawn (manual button) keeps the flock visible immediately;
    // the ambient timer-driven spawns start far away so they fade in across
    // the horizon.
    const startDist = opts.closeSpawn ? 90 : 450;
    const lateralMax = opts.closeSpawn ? 50 : 220;
    const altLow = opts.closeSpawn ? 14 : 25;
    const altSpan = opts.closeSpawn ? 12 : 35;

    const lateral = (Math.random() * 2 - 1) * lateralMax;
    const headX = centerX + sideX * lateral - fwdX * startDist;
    const headZ = centerZ + sideZ * lateral - fwdZ * startDist;
    const altitude = waterLevel + altLow + Math.random() * altSpan;
    const speed = 10 + Math.random() * 4; // 10..14 m/s
    const velX = fwdX * speed;
    const velZ = fwdZ * speed;
    // Yaw such that local +X aligns with world velocity (see shader rotation).
    const yaw = Math.atan2(-velZ, velX);

    // 60% solo gull, 40% small V-flock. `forceFlock` overrides for the
    // manual "release birds" button so the user always sees a flock.
    const isFlock = opts.forceFlock || Math.random() < 0.4;
    const flockSize = isFlock ? 3 + ((Math.random() * 4) | 0) : 1;
    const birds: Bird[] = [];
    if (flockSize === 1) {
      birds.push({
        phase: Math.random() * Math.PI * 2,
        offAlong: 0,
        offSide: 0,
        offUp: 0,
      });
    } else {
      // Leader.
      birds.push({
        phase: Math.random() * Math.PI * 2,
        offAlong: 0,
        offSide: 0,
        offUp: 0,
      });
      // Alternate left/right wings of the V.
      const spacing = 3.0;
      for (let i = 1; i < flockSize; i++) {
        const goLeft = i % 2 === 1;
        const rank = (i + 1) >> 1; // 1, 1, 2, 2, 3, 3
        const sideSign = goLeft ? 1 : -1;
        birds.push({
          phase: Math.random() * Math.PI * 2,
          offAlong: -rank * spacing,
          offSide: sideSign * rank * spacing,
          offUp: (Math.random() - 0.5) * 0.6,
        });
      }
    }

    // Slightly bigger silhouette for solo birds so they read against the sky.
    const scale = flockSize === 1 ? 1.4 : 1.1;
    const duration = (startDist * 2) / speed; // travel through the corridor

    this.flights.push({
      headX,
      headY: altitude,
      headZ,
      velX,
      velZ,
      yaw,
      fwdX,
      fwdZ,
      sideX,
      sideZ,
      scale,
      elapsed: 0,
      duration,
      birds,
    });
  }
}
